import { useEffect, useState } from "react";
import { _post } from "../../../libraries/fetch";
import { getDesktopOnlineStatusURL } from "../../../libraries/apiUrl";

const MIN_DATE = "2020-01-01";
const MAX_DATE = "2050-12-31";

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftDate = (dateString, days) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day + days);
  return getLocalDateString(date);
};

const formatDisplayDate = (dateString) => {
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

const dateInputStyle = {
  padding: "10px",
  fontSize: "14px",
  borderRadius: "5px",
  border: "1px solid #24272f",
  backgroundColor: "#363b45",
  color: "#fff"
};

function Analytics({ branchId }) {
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  useEffect(() => {
    if (!isTimelineOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsTimelineOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isTimelineOpen]);

  const isInfoMessage = (message) => {
    if (!message || typeof message !== 'string') return false;
    const msgLower = message.toLowerCase();
    return (
      message === 'No data Found' ||
      message.includes('No DesktopAppRuntime') ||
      msgLower.includes('desktopappruntime') ||
      msgLower.includes('no data') ||
      msgLower.includes('not found') ||
      (msgLower.includes('no') && msgLower.includes('exist'))
    );
  };

  const applyStatusResponse = (response, { openTimeline = false } = {}) => {
    let errorMessage = null;

    if (response && response.error) {
      errorMessage = response.error;
    } else if (response && response.message) {
      errorMessage = response.message;
    } else if (response && typeof response.response === "string") {
      errorMessage = response.response;
    } else if (response && response.response && typeof response.response === "string") {
      errorMessage = response.response;
    }

    if (errorMessage && (
      errorMessage.includes("No DesktopAppRuntime") ||
      errorMessage.includes("DesktopAppRuntime") ||
      errorMessage.toLowerCase().includes("no data") ||
      errorMessage.toLowerCase().includes("not found")
    )) {
      setError(errorMessage);
      setStatusData(null);
      return;
    }

    if (errorMessage) {
      setError(errorMessage || "An error occurred");
      setStatusData(null);
      return;
    }

    let responseData = null;

    if (response && response.response !== undefined) {
      responseData = response.response;
    } else if (response) {
      responseData = response;
    }

    if (responseData === null || responseData === undefined) {
      setError("No data Found");
      setStatusData(null);
      return;
    }

    if (typeof responseData === "string") {
      setError(responseData);
      setStatusData(null);
      return;
    }

    const hasFirstHalf = responseData.firstHalf !== undefined;
    const hasSecondHalf = responseData.secondHalf !== undefined;
    const hasThirdHalf = responseData.thirdHalf !== undefined;
    const hasFourthHalf = responseData.fourthHalf !== undefined;

    if (hasFirstHalf && hasSecondHalf && hasThirdHalf && hasFourthHalf) {
      if (
        Array.isArray(responseData.firstHalf) &&
        Array.isArray(responseData.secondHalf) &&
        Array.isArray(responseData.thirdHalf) &&
        Array.isArray(responseData.fourthHalf)
      ) {
        setStatusData(responseData);
        setError(null);
        if (openTimeline) {
          setIsTimelineOpen(true);
        }
      } else {
        setError(`Invalid data format: Expected arrays but got different types. Keys found: ${Object.keys(responseData).join(", ")}`);
        setStatusData(null);
      }
    } else {
      const missingKeys = [];
      if (!hasFirstHalf) missingKeys.push("firstHalf");
      if (!hasSecondHalf) missingKeys.push("secondHalf");
      if (!hasThirdHalf) missingKeys.push("thirdHalf");
      if (!hasFourthHalf) missingKeys.push("fourthHalf");
      setError(`Invalid data format: Missing keys [${missingKeys.join(", ")}]. Available keys: ${Object.keys(responseData).join(", ")}`);
      setStatusData(null);
    }
  };

  const fetchStatusForDate = (date, { openTimeline = false, keepTimelineOpen = false } = {}) => {
    if (!branchId) {
      setError("Branch ID is required");
      return;
    }

    setLoading(true);
    setError(null);
    setStatusData(null);

    if (!keepTimelineOpen) {
      setIsTimelineOpen(false);
    }

    const data = { date };

    _post(getDesktopOnlineStatusURL(branchId), data)
      .then((response) => {
        setLoading(false);
        applyStatusResponse(response, { openTimeline: openTimeline || keepTimelineOpen });
      })
      .catch(() => {
        setLoading(false);
        setError("Failed to load desktop online status");
        setStatusData(null);
      });
  };

  const handleSubmit = () => {
    fetchStatusForDate(selectedDate, { openTimeline: true });
  };

  const handleDateNavigation = (direction) => {
    const today = getLocalDateString();
    const newDate = shiftDate(selectedDate, direction);

    if (newDate < MIN_DATE || newDate > MAX_DATE) {
      return;
    }

    if (direction > 0 && newDate > today) {
      return;
    }

    setSelectedDate(newDate);
    fetchStatusForDate(newDate, { keepTimelineOpen: true });
  };

  const isNextDateDisabled = loading || selectedDate >= getLocalDateString();

  const getSegmentStatus = (hour, segmentIndex) => {
    // segmentIndex: 0 (0-14 min = firstHalf), 1 (15-29 min = secondHalf), 2 (30-44 min = thirdHalf), 3 (45-59 min = fourthHalf)
    if (!statusData) {
      return 'unknown';
    }

    let segmentArray = null;
    // Map segment index to the correct array
    if (segmentIndex === 0) {
      segmentArray = statusData.firstHalf;
    } else if (segmentIndex === 1) {
      segmentArray = statusData.secondHalf;
    } else if (segmentIndex === 2) {
      segmentArray = statusData.thirdHalf;
    } else if (segmentIndex === 3) {
      segmentArray = statusData.fourthHalf;
    }

    // Check if array exists and has data for this hour
    if (!Array.isArray(segmentArray) || hour >= segmentArray.length) {
      return 'unknown';
    }

    // 0 = offline/red, 1 = online/green
    const value = segmentArray[hour];
    return value === 1 ? 'online' : (value === 0 ? 'offline' : 'unknown');
  };

  const renderStatusBar = (compact = false) => {
    // Generate hours from 00:00 to 23:00
    const hours = Array.from({ length: 24 }, (_, i) => {
      const hourStr = String(i).padStart(2, '0');
      return `${hourStr}:00`;
    });

    return hours.map((time, index) => {
      const hourNum = parseInt(time.split(':')[0]);

      // Get status for each of the 4 segments (15 minutes each)
      const segment0 = getSegmentStatus(hourNum, 0); // 0-14 min
      const segment1 = getSegmentStatus(hourNum, 1); // 15-29 min
      const segment2 = getSegmentStatus(hourNum, 2); // 30-44 min
      const segment3 = getSegmentStatus(hourNum, 3); // 45-59 min

      return (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: compact ? 0 : '10px',
          padding: compact ? 0 : '5px 0'
        }}>
          <div style={{
            width: compact ? '60px' : '80px',
            fontSize: compact ? '12px' : '14px',
            color: '#fff',
            marginRight: compact ? '10px' : '15px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center'
          }}>
            {time}
          </div>
          <div style={{
            flex: 1,
            minWidth: 0,
            height: compact ? '35px' : '30px',
            display: 'flex',
            borderRadius: '4px',
            overflow: 'hidden',
            border: '1px solid #24272f',
            gap: '2px'
          }}>
            {/* Segment 1: 0-14 minutes */}
            <div style={{
              width: '25%',
              height: '100%',
              backgroundColor: segment0 === 'online' ? '#187718' : (segment0 === 'offline' ? '#d51717' : '#363b45'),
              transition: 'background-color 0.3s ease'
            }}></div>

            {/* Segment 2: 15-29 minutes */}
            <div style={{
              width: '25%',
              height: '100%',
              backgroundColor: segment1 === 'online' ? '#187718' : (segment1 === 'offline' ? '#d51717' : '#363b45'),
              transition: 'background-color 0.3s ease'
            }}></div>

            {/* Segment 3: 30-44 minutes */}
            <div style={{
              width: '25%',
              height: '100%',
              backgroundColor: segment2 === 'online' ? '#187718' : (segment2 === 'offline' ? '#d51717' : '#363b45'),
              transition: 'background-color 0.3s ease'
            }}></div>

            {/* Segment 4: 45-59 minutes */}
            <div style={{
              width: '25%',
              height: '100%',
              backgroundColor: segment3 === 'online' ? '#187718' : (segment3 === 'offline' ? '#d51717' : '#363b45'),
              transition: 'background-color 0.3s ease'
            }}></div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="configMain" style={{ paddingBottom: '30px', overflow: 'hidden' }}>
      <h1 style={{ margin: 0, marginBottom: '20px' }}>Desktop Online Status</h1>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginTop: '20px',
        marginBottom: '30px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={MIN_DATE}
            max={MAX_DATE}
            style={{
              ...dateInputStyle,
              cursor: 'pointer',
              width: 'min(200px, 100%)'
            }}
          />
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: '12px 30px',
            fontSize: '16px',
            fontWeight: 'bold',
            backgroundColor: '#187718',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#1a8a1a';
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = '#187718';
            }
          }}
        >
          {loading ? 'Loading...' : 'Submit'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: isInfoMessage(error) ? '#187718' : '#d51717',
          color: '#fff',
          borderRadius: '5px',
          marginBottom: '20px',
          fontSize: '16px',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}

      {isTimelineOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: "100vh",
            zIndex: 9999,
            backgroundColor: "#1b1e25",
            display: "flex",
            flexDirection: "column",
            padding: "clamp(12px, 3vw, 16px) clamp(12px, 3vw, 24px)",
            boxSizing: "border-box",
            overflow: "auto"
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="timeline-title"
        >
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "10px",
            flexShrink: 0
          }}>
            <h2
              id="timeline-title"
              style={{
                color: "#fff",
                margin: 0,
                fontSize: "clamp(16px, 4vw, 20px)",
                fontWeight: "bold",
                flex: "1 1 200px"
              }}
            >
              Desktop Online Status Timeline
            </h2>
            <div style={{
              ...dateInputStyle,
              fontSize: "clamp(14px, 3.5vw, 18px)",
              fontWeight: "bold",
              textAlign: "center",
              minWidth: "min(220px, 100%)",
              padding: "12px 24px",
              flex: "1 1 auto"
            }}>
              Date : {formatDisplayDate(selectedDate)}
              {loading ? " (Loading...)" : ""}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end", flex: "1 1 auto" }}>
              <button
                type="button"
                onClick={() => handleDateNavigation(-1)}
                disabled={loading || selectedDate <= MIN_DATE}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  backgroundColor: loading || selectedDate <= MIN_DATE ? "#3b3f47" : "#187718",
                  color: "#fff",
                  border: "1px solid #444",
                  borderRadius: "5px",
                  cursor: loading || selectedDate <= MIN_DATE ? "not-allowed" : "pointer",
                  opacity: loading || selectedDate <= MIN_DATE ? 0.6 : 1
                }}
              >
                Previous Date
              </button>
              <button
                type="button"
                onClick={() => handleDateNavigation(1)}
                disabled={isNextDateDisabled}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  backgroundColor: isNextDateDisabled ? "#3b3f47" : "#187718",
                  color: "#fff",
                  border: "1px solid #444",
                  borderRadius: "5px",
                  cursor: isNextDateDisabled ? "not-allowed" : "pointer",
                  opacity: isNextDateDisabled ? 0.6 : 1
                }}
              >
                Next Date
              </button>
              <button
                type="button"
                onClick={() => setIsTimelineOpen(false)}
                style={{
                  padding: "10px 24px",
                  fontSize: "15px",
                  fontWeight: "bold",
                  backgroundColor: "#d51717",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              padding: "12px 15px",
              backgroundColor: isInfoMessage(error) ? "#187718" : "#d51717",
              color: "#fff",
              borderRadius: "5px",
              marginBottom: "10px",
              fontSize: "14px",
              fontWeight: "500",
              flexShrink: 0
            }}>
              {error}
            </div>
          )}

          <div style={{
            display: "flex",
            gap: "16px",
            marginBottom: "10px",
            flexShrink: 0
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fff", fontSize: "14px" }}>
              <span style={{ width: "16px", height: "16px", backgroundColor: "#187718", borderRadius: "3px", display: "inline-block" }} />
              Online
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fff", fontSize: "14px" }}>
              <span style={{ width: "16px", height: "16px", backgroundColor: "#d51717", borderRadius: "3px", display: "inline-block" }} />
              Offline
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fff", fontSize: "14px" }}>
              <span style={{ width: "16px", height: "16px", backgroundColor: "#363b45", borderRadius: "3px", display: "inline-block" }} />
              No Data
            </span>
          </div>

          <div style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "5px",
            backgroundColor: "#282c34",
            borderRadius: "8px",
            border: "1px solid #24272f",
            padding: "12px 16px"
          }}>
            {statusData ? renderStatusBar(true) : (
              <p style={{ color: "#aaa", textAlign: "center", margin: 0 }}>
                {loading ? "Loading status..." : "No status data available for this date."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;

